<template>
  <v-row no-gutters>
    <v-col cols="12">
      <v-skeleton-loader v-if="$store.state.receivables.retrieveLoader" transition="fade-transition" type="table" />
      <template v-else>
        <v-col v-if="!agreementsList.length" class="text-center py-5">
          <v-icon class="d-block" color="secondary" size="70">mdi-handshake</v-icon>
          <span class="d-block subtitle-1">No existen acuerdos</span>
          <v-btn class="my-5" v-if="showCreate" @click="$emit('create')" outlined>Registrar un acuerdo</v-btn>
        </v-col>
        <template v-else>
          <v-data-table :headers="headers" :items="agreementsList" :items-per-page="pagination" hide-default-header disable-sort mobile-breakpoint hide-default-footer @click:row="(agreementsList) => { hasLink ? $router.push({name: 'InvoiceRetrieve', params: {id: agreementsList.invoice.id}}) : ''}" style="cursor: pointer">
            <!-- header -->
            <template v-slot:header="{ props: { headers } }">
              <VTableHeaders :headers="headers" @sort="setSort" />
            </template>
            <!-- end header -->
            <template v-slot:[`item.invoice__customer__name`]="{item}">
              <div class="text-left py-4">
                <v-menu open-on-hover right offset-x :nudge-width="160" transition="slide-x-transition">
                  <template v-slot:activator="{on}">
                    <div v-on="on">
                      <span class="text--secondary font-weight-medium">{{item.invoice.customer.name}}</span>
                    </div>
                  </template>
                  <v-card class="pa-0">
                    <v-subheader class="font-weight-medium background">{{item.invoice.customer.name}}</v-subheader>
                    <v-card-text class="pa-0">
                      <v-row class="mx-0">
                        <v-col cols="12" class="py-2" v-if="item.note">
                          <span class="d-block text--secondary font-weight-medium">Nota interna</span>
                          <span class="d-block">{{item.note}}</span>
                        </v-col>
                        <v-col cols="12" class="py-2" v-else>
                          <span class="d-block font-italic text--disabled">Sin nota interna</span>
                        </v-col>
                      </v-row>
                    </v-card-text>
                    <v-divider class="mx-3" />
                    <v-card-actions>
                      <v-spacer />
                      <v-btn :to="{name: 'CustomerRetrieveUpdate', params: {id: item.invoice.customer.id}}" text color="primary">Ver cliente <v-icon small right>mdi-arrow-right</v-icon></v-btn>
                      <v-spacer />
                    </v-card-actions>
                  </v-card>
                </v-menu>
              </div>
            </template>
            <template v-slot:[`item.status`]="{item}">
              <v-row align="center" justify="end">
                <div class="text-right" v-if="showStatus">
                  <v-chip v-if="item.status === 'cancelled'" class="mx-1" color="defaultGrey" text-color="secondary" small>Anulado<v-icon class="ml-1" x-small>mdi-close</v-icon></v-chip>
                  <v-chip v-else-if="item.status === 'superseded'" class="mx-1" color="defaultGrey" text-color="secondary" small>Reemplazado</v-chip>
                  <v-chip v-else-if="item.invoice.status === 'paid'" class="mx-1" color="defaultGrey" text-color="secondary" small>Cumplido<v-icon class="ml-1" x-small>mdi-check</v-icon></v-chip>
                  <v-chip v-else-if="item.invoice.status === 'cancelled'" class="mx-1" color="defaultGrey" text-color="secondary" small>Factura anulada</v-chip>
                  <v-chip v-else-if="item.date < today" class="mx-1" color="defaultGrey" text-color="secondary" small>Acuerdo vencido</v-chip>
                  <v-chip v-else class="mx-1" color="lightBlue" text-color="primary" small>Vigente</v-chip>
                </div>
                <v-tooltip color="primary" dark max-width="280" top transition="scale-transition">
                  <template v-slot:activator="{on}">
                    <v-icon v-on="on" class="ml-3" color="secondary" small>{{item.origin === 'agent' ? 'mdi-robot' : 'mdi-account-circle'}}</v-icon>
                  </template>
                  <span class="white--text">por {{item.origin === 'agent' ? 'un agente' : item.user}} el {{item.created | dateTime}}</span>
                </v-tooltip>
              </v-row>
            </template>
            <template v-slot:[`item.invoice__number`]="{item}">
              <div class="text-left py-2">
                <v-menu open-on-hover right offset-x :nudge-width="160" transition="slide-x-transition">
                  <template v-slot:activator="{on}">
                    <div v-on="on">
                      <span class="d-block">{{item.invoice.document.name}}</span>
                      <span class="d-block body-2 text--secondary font-weight-medium" v-if="!['draft', 'pending'].includes(item.invoice.status)">Nº {{item.invoice.number}}</span>
                    </div>
                  </template>
                  <v-card>
                    <v-subheader class="font-weight-medium background">{{item.invoice.document.name}}</v-subheader>
                    <v-card-text>
                      <v-list-item class="px-0">
                        <v-list-item-title class="text--secondary font-weight-medium">Productos</v-list-item-title>
                      </v-list-item>
                      <template v-if="item.invoice.lines.length">
                        <v-list-item class="px-0 my-1" v-for="p in item.invoice.lines.slice(0, 3)" :key="p.id">
                          <v-list-item-title>
                            <span class="d-block">{{p.name}}</span>
                            <span class="d-block font-italic text--disabled">{{p.quantity}} unidades</span>
                          </v-list-item-title>
                          <v-list-item-subtitle class="text-right">{{p.amount | currency(item.invoice.currency)}} {{item.invoice.currency}}</v-list-item-subtitle>
                        </v-list-item>
                      </template>
                      <v-list-item class="px-0 my-1" v-if="item.invoice.lines.length > 3">
                        <v-list-item-title>y otros {{item.invoice.lines.length - 3}} productos</v-list-item-title>
                      </v-list-item>
                    </v-card-text>
                    <v-divider class="mx-4" />
                    <v-card-actions>
                      <v-spacer />
                      <v-btn :to="{name: 'InvoiceRetrieve', params: {id: item.invoice.id}}" text color="primary">Ver detalle <v-icon small right>mdi-arrow-right</v-icon></v-btn>
                      <v-spacer />
                    </v-card-actions>
                  </v-card>
                </v-menu>
              </div>
            </template>
            <template v-slot:[`item.invoice__due_date`]="{item}">
              <span v-if="item.invoice.due_date">{{item.invoice.due_date | date}}</span>
              <span v-else>---</span>
            </template>
            <template v-slot:[`item.date`]="{item}">
              <span class="text--secondary font-weight-medium">{{item.date | date}}</span>
            </template>
            <template v-slot:[`item.invoice__total`]="{item}">
              <div class="text-right">
                <span class="body-2 text--secondary font-weight-medium">{{item.invoice.total | currency(item.invoice.currency)}}</span>
                <span class="ml-1">{{item.invoice.currency}}</span>
              </div>
            </template>
            <template v-slot:[`item.actions`]="{item}">
              <v-menu v-if="item.status === 'active'" close-on-click close-on-content-click left offset-y :nudge-bottom="6" transition="slide-y-transition">
                <template v-slot:activator="{on, attrs}">
                  <v-btn @click.stop v-bind="attrs" v-on="on" icon small>
                    <v-icon color="secondary" size="24">mdi-dots-horizontal</v-icon>
                  </v-btn>
                </template>
                <v-list class="pa-0" dense>
                  <v-list-item v-if="isFromToday(item)" @click.stop="destroy(item)" :ripple="false">
                    <v-list-item-title class="subtitle-2 font-weight-medium red--text">Eliminar</v-list-item-title>
                  </v-list-item>
                  <v-list-item v-else @click.stop="cancel(item)" :ripple="false">
                    <v-list-item-title class="subtitle-2 font-weight-medium red--text">Anular acuerdo</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </template>
          </v-data-table>
          <template v-if="showPagination">
            <v-divider />
            <v-row class="mx-0" align="center">
              <v-col cols="6">
                <span class="body-2 text--secondary">{{count}} registros</span>
              </v-col>
              <v-col cols="6" class="text-right">
                <v-pagination v-model="page" :length="totalPages" total-visible="6" />
              </v-col>
            </v-row>
          </template>
        </template>
      </template>
    </v-col>
  </v-row>
</template>
<script>
import moment from 'moment'
import { mapState } from 'vuex'

import VTableHeaders from '@/components/VTable/VTableHeaders'

import ListViewMixin from '@/mixins/ListViewMixin'

export default {
  components: {
    VTableHeaders
  },
  mixins: [
    ListViewMixin
  ],
  data: () => ({
    // los value son claves de ordenamiento: ListViewMixin.setSort los empuja
    // tal cual a ?ordering= y VTableHeaders los lee de vuelta para la flecha
    headers: [
      { text: 'Cliente', value: 'invoice__customer__name', class: 'font-weight-bold', sortable: true },
      { text: '', value: 'status' },
      { text: 'Documento', value: 'invoice__number' },
      { text: 'Vencimiento', value: 'invoice__due_date', align: 'end', sortable: true },
      { text: 'Fecha acordada', value: 'date', align: 'end', sortable: true },
      { text: 'Total', value: 'invoice__total', align: 'end', sortable: true },
      { text: '', value: 'actions', align: 'end', width: 48 }
    ]
  }),
  computed: mapState({
    count: state => state.receivables.agreementsCount,
    agreementsList: state => state.receivables.agreementsList
  }),
  methods: {
    isFromToday (item) {
      return moment(item.created).format('YYYY-MM-DD') === this.today
    },
    destroy (item) {
      // solo los de hoy se borran; el resto se anula y queda registrado, para
      // poder explicar después por qué salieron o no los recordatorios
      this.$dialog.warning({
        title: 'Eliminar acuerdo',
        text: `
          <span class="d-block mb-4">Se eliminará el acuerdo y la cobranza de este documento volverá a seguir su fecha de vencimiento.</span>
          <span class="d-block font-weight-medium">Esta acción no se puede deshacer</span>
        `,
        actions: [
          {
            color: 'red',
            text: 'Continuar',
            handle: () => {
              this.$store.dispatch('receivables/DESTROY', {
                resource: 'agreements',
                id: item.id
              })
              .then(() => {
                this.$dialog.message.info('El acuerdo ha sido eliminado')
                this.$emit('change')
              })
              .catch((error) => {
                this.$dialog.message.error(this.parseErrors(error.response.data))
              })
            }
          }
        ]
      })
    },
    cancel (item) {
      this.$dialog.warning({
        title: 'Anular acuerdo',
        text: `
          <span class="d-block mb-4">El acuerdo quedará registrado como anulado y la cobranza de este documento volverá a seguir su fecha de vencimiento.</span>
          <span class="d-block">¿Estás seguro de continuar?</span>
        `,
        actions: [
          {
            color: 'red',
            text: 'Continuar',
            handle: () => {
              this.$store.dispatch('receivables/CREATE', {
                resource: 'agreements',
                id: item.id,
                detail: 'cancel',
                payload: {},
                commitInstance: false
              })
              .then(() => {
                this.$dialog.message.info('El acuerdo ha sido anulado')
                this.$emit('change')
              })
              .catch((error) => {
                this.$dialog.message.error(this.parseErrors(error.response.data))
              })
            }
          }
        ]
      })
    }
  }
}
</script>
